import React, { Fragment, memo, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { green as blue } from '@mui/material/colors';
import { red } from '@mui/material/colors';
import { PRESCRIPTION_UNIT } from 'js/constants/PrescriptionConstants';
import { usePrescriptionJob } from 'js/components/Prescription/PrescriptionJobContext';
import Box from '@mui/material/Box';
import NumberUtils from 'js/helpers/NumberUtils';
import useEvent from 'js/hooks/useEvent';
const tinycolor = require('tinycolor2');

import style from './IntervalVisualizer.module.less';
import { grey } from '@mui/material/colors';
import PercentageIncrementer from '../PercentageIncrementer/PercentageIncrementer';
import {
  getIntervalUniqueValues,
  getIntervalDisplayBounds,
  SQUARE_METER_COVERSION_VALUE,
  getEffectiveSoilValues,
} from '../PrescriptionUtils';
import { isSoilLayer } from '../PrescriptionJob';
import { getPrescriptionIntervalColor, isNullInterval } from '../nullValueRegions';

const canSplitInterval = (interval) => {
  return interval.max - interval.min > 0.05;
};

const canSplitSoilInterval = (interval, values) => {
  const uniqueValues = getIntervalUniqueValues(interval, values);
  return uniqueValues.length > 1;
};

const IntervalVisualizer = ({
  onClick,
  wrapperRect,
  mergingIntervals,
  splittingIntervals,
  mergePrimaryIndex,
  mergeSecondaryIndex,
  splittingIndex,
  onIncrementInterval,
  onDecrementInterval,
  stepSize,
  isBulkPrescription,
  appliedInitialIntervals,
  bulkPrescriptionJobs,
}) => {
  const { prescriptionJob } = usePrescriptionJob();
  const { intervals, maxPrescription, fieldSize } = prescriptionJob;
  const isBulkMode = isBulkPrescription && !appliedInitialIntervals;
  const effectiveValues = useMemo(
    () =>
      getEffectiveSoilValues(
        prescriptionJob.values,
        prescriptionJob.layer,
        isBulkMode,
        bulkPrescriptionJobs
      ),
    [prescriptionJob.values, prescriptionJob.layer, isBulkMode, bulkPrescriptionJobs]
  );
  const [highlightPrimaryBar, setHighlightPrimaryBar] = useState(null);
  const [highlightSecondaryIndex, setHighlightSecondaryIndex] = useState(null);
  const [mouse, setMouse] = useState(null);

  const handleMouseEnter = useCallback(
    (event, bar) => {
      const interval = intervals[bar.index];

      // Restrictions when merging intervals
      if (mergingIntervals) {
        if (isNullInterval(interval)) {
          setHighlightPrimaryBar(null);
          setHighlightSecondaryIndex(null);
          return;
        }
        if (intervals.length === 1) {
          setHighlightPrimaryBar(null);
          setHighlightSecondaryIndex(null);
          return;
        }
      }

      // Restrictions when splitting intervals
      if (splittingIntervals) {
        // Only allow splitting of soil intervals which contains more than one classification
        if (
          isSoilLayer(prescriptionJob.layer) &&
          !canSplitSoilInterval(interval, effectiveValues)
        ) {
          setHighlightPrimaryBar(null);
          setHighlightSecondaryIndex(null);
          return;
        }
        // Only allow splitting of bars that will not become too small to handle in the editor
        if (!canSplitInterval(interval)) {
          setHighlightPrimaryBar(null);
          setHighlightSecondaryIndex(null);
          return;
        }
      }

      if (mergingIntervals || splittingIntervals) {
        setHighlightPrimaryBar(bar);
        setHighlightSecondaryIndex(null);
      }
    },
    [intervals, mergingIntervals, splittingIntervals]
  );

  const handleMouseLeave = useCallback(() => {
    setHighlightPrimaryBar(null);
    setHighlightSecondaryIndex(null);
  });

  const handleMouseUp = useCallback(
    (event) => {
      event.persist();

      const target = event.currentTarget;

      if (splittingIntervals) {
        if (highlightPrimaryBar) {
          onClick(target, highlightPrimaryBar.index);
        }
      }

      if (mergingIntervals) {
        if (highlightPrimaryBar && highlightSecondaryIndex) {
          onClick(target, highlightPrimaryBar.index, highlightSecondaryIndex);
        }
      }
    },
    [splittingIntervals, highlightPrimaryBar, onClick, mergingIntervals, highlightSecondaryIndex]
  );

  const getIntervalProperties = useCallback(
    (interval, idx) => {
      let centerVal = maxPrescription / fieldSize;
      if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
        centerVal =
          prescriptionJob.maxPrescription /
          (prescriptionJob.fieldSize * SQUARE_METER_COVERSION_VALUE);
      }

      const valueMax = centerVal * 2;
      const value = interval.prescription;

      const soilLayer = isSoilLayer(prescriptionJob.layer);
      const { left, width } = getIntervalDisplayBounds(
        prescriptionJob.intervals,
        idx,
        soilLayer ? effectiveValues : null
      );

      // Size and positioning
      const height = NumberUtils.limitMap(value, 0, valueMax, 0, 100);

      return {
        value: value,
        width: width,
        height: height,
        left: left,
        bottom: 0,
      };
    },
    [prescriptionJob]
  );

  const getRectStyles = useCallback(
    (idx, interval) => {
      const color = tinycolor(getPrescriptionIntervalColor(interval, idx, intervals));
      const error = !canSplitInterval(interval) && splittingIntervals;

      let bgColor = color.clone();
      bgColor.setAlpha(0.1);

      let fgColor = color.clone();

      const backgroundStyle = {
        backgroundColor: bgColor,
        pointerEvents: 'all',
      };

      const foregroundStyle = {
        backgroundColor: fgColor,
        pointerEvents: 'none',
      };

      // If this bar is highlighted as the primary target for merge or split
      const isTargetingPrimary = highlightPrimaryBar && highlightPrimaryBar.index === idx;
      const isMergingPrimary = mergePrimaryIndex === idx;
      const isSplitting = splittingIntervals && splittingIndex === idx;

      const isPrimary = isTargetingPrimary || isMergingPrimary || isSplitting;

      if (splittingIntervals || mergingIntervals) {
        backgroundStyle.backgroundColor = 'none';

        if (error) {
          bgColor = tinycolor(red['200']);
          bgColor.setAlpha(0.1);

          fgColor = tinycolor(grey['200']);

          backgroundStyle.backgroundColor = bgColor;
          foregroundStyle.backgroundColor = fgColor;
        }
      }

      if (isPrimary && !error) {
        bgColor = tinycolor(blue['A100']);
        bgColor.setAlpha(0.7);

        backgroundStyle.backgroundColor = bgColor;
        backgroundStyle.cursor = 'pointer';
      }

      // If this bar is the secondary target for merge or split
      const isTargetingSecondary = highlightPrimaryBar && highlightSecondaryIndex === idx;
      const isMergingSecondary = mergeSecondaryIndex === idx;
      const isSecondary = isTargetingSecondary || isMergingSecondary;

      if (isSecondary && !error) {
        bgColor = tinycolor(blue['A100']);
        bgColor.setAlpha(0.7);

        backgroundStyle.backgroundColor = bgColor;
      }

      return {
        backgroundStyle: backgroundStyle,
        foregroundStyle: foregroundStyle,
        isPrimary: isPrimary,
        isSecondary: isSecondary,
        error: error,
      };
    },
    [
      intervals,
      highlightPrimaryBar,
      highlightSecondaryIndex,
      mergePrimaryIndex,
      mergeSecondaryIndex,
      splittingIntervals,
      splittingIndex,
      mergingIntervals,
    ]
  );

  const renderRectangles = useCallback(
    (idx, properties, styles) => {
      const { width, height, left, bottom } = properties;

      // Get styling properties for the two rectangles
      const { backgroundStyle, foregroundStyle, isPrimary, isSecondary } = styles;

      const interval = intervals[idx];

      // Group properties required by the mouse-listeners
      const bar = {
        index: idx,
        interval: interval,
        left: left,
        bottom: bottom,
        width: width,
        height: height,
      };

      const backgroundRect = (
        <Box
          key={idx + ' background'}
          onMouseEnter={(event) => handleMouseEnter(event, bar)}
          onMouseLeave={() => handleMouseLeave()}
          onMouseUp={(event) => handleMouseUp(event)}
          style={backgroundStyle}
          sx={{
            position: 'absolute',
            left: `${left}%`,
            bottom: `0%`,
            width: `${width}%`,
            height: `100%`,
          }}
        >
          <PercentageIncrementer
            idx={idx}
            interval={interval}
            stepSize={stepSize}
            onIncrementInterval={onIncrementInterval}
            onDecrementInterval={onDecrementInterval}
          />
        </Box>
      );

      const foregroundRect = (
        <Box
          key={idx}
          style={foregroundStyle}
          sx={{
            position: 'absolute',
            left: `${left}%`,
            bottom: `${bottom}%`,
            width: `${width}%`,
            height: `${height}%`,
          }}
        />
      );

      // Evaluate ordering of the rectangles
      if (isPrimary || isSecondary) {
        return [foregroundRect, backgroundRect];
      } else {
        return [backgroundRect, foregroundRect];
      }
    },
    [
      intervals,
      handleMouseEnter,
      handleMouseLeave,
      handleMouseUp,
      onIncrementInterval,
      onDecrementInterval,
      stepSize,
    ]
  );

  useEvent(
    'mousemove',
    useCallback(
      (event) => {
        if (mergingIntervals || splittingIntervals) {
          setMouse({ x: event.clientX, y: event.clientY });
        }
      },
      [mergingIntervals, splittingIntervals]
    )
  );

  useEffect(() => {
    // If we don't have a primary target or we are splitting intervals, then don't highlight a secondary target
    if (!highlightPrimaryBar || splittingIntervals || !wrapperRect || !mouse) {
      return;
    }

    const { xPos, width, index } = highlightPrimaryBar;

    const left = wrapperRect.left || 0;
    const x = mouse.x - Math.max(left, 0);

    const middle = xPos + width / 2;

    const previous = index - 1;
    const next = index + 1;
    const hasPrevious = previous >= 0;
    const hasNext = next < intervals.length;

    let secondaryIndex = 0;

    if (x <= middle) {
      if (hasPrevious) {
        secondaryIndex = previous;
      } else {
        secondaryIndex = next;
      }
    } else {
      if (hasNext) {
        secondaryIndex = next;
      } else {
        secondaryIndex = previous;
      }
    }

    setHighlightSecondaryIndex(isNullInterval(intervals[secondaryIndex]) ? null : secondaryIndex);
  }, [mouse, highlightPrimaryBar, splittingIntervals, intervals, wrapperRect]);

  useEffect(() => {
    if (
      (!mergingIntervals && !splittingIntervals) ||
      !mergePrimaryIndex ||
      !splittingIndex ||
      intervals.length === 1
    ) {
      setHighlightPrimaryBar(null);
      setHighlightSecondaryIndex(null);
    }
  }, [mergingIntervals, splittingIntervals, mergePrimaryIndex, splittingIndex, intervals]);

  return intervals.map((interval, idx) => {
    const styles = getRectStyles(idx, interval);
    const properties = getIntervalProperties(interval, idx);

    const isSplitting =
      (splittingIntervals && highlightPrimaryBar && highlightPrimaryBar.index === idx) ||
      splittingIndex === idx;

    let splitLeft;
    if (isSoilLayer(prescriptionJob.layer)) {
      const intervalUniqueValues = getIntervalUniqueValues(interval, effectiveValues);
      splitLeft = properties.left + properties.width / intervalUniqueValues.length;
    } else {
      splitLeft = properties.left + properties.width / 2;
    }

    return (
      <Fragment key={`interval-visualizer-fragment-${idx}`}>
        {renderRectangles(idx, properties, styles)}

        {isSplitting && (
          <Box
            className={style.SplitLine}
            sx={{
              bottom: 0,
              top: 0,
              left: `${splitLeft}%`,
            }}
          />
        )}
      </Fragment>
    );
  });
};

IntervalVisualizer.propTypes = {
  onClick: PropTypes.func,
  onIncrementInterval: PropTypes.func,
  onDecrementInterval: PropTypes.func,
  mergePrimaryIndex: PropTypes.number,
  mergeSecondaryIndex: PropTypes.number,
  splittingIndex: PropTypes.number,
  stepSize: PropTypes.number,
  mergingIntervals: PropTypes.bool,
  splittingIntervals: PropTypes.bool,
  wrapperRect: PropTypes.any,
};

export default memo(IntervalVisualizer);
